import React, { useState } from "react";
import styled from "styled-components";
import SearchBar from "../../components/shared-components/SearchBar";
import { AIRankingNFTs } from "../../pages/Home/Types";
import { useNavigate } from "react-router-dom";
import Loader from "../../components/shared-components/Loader";
import FilterForm from "../Marketplace/FilterForm";
import { FilterWrapperCard } from "../Marketplace/style";
import HeaderWithFilter from "./HeaderWithFilter";
import { IconButtonSmall } from "../../components/ButtonsNew";
import {
  ArrowForwardOrange,
  BlankImage,
  FireIcon,
  LeafIcon,
} from "../../shared/icons";
import { Skeleton } from "@mui/material";
import { screenSizes } from "../../styles/theme";
import { RootState } from "../../store";
import { useSelector } from "react-redux";
import { getValidImageUrl } from "../../utils";

const CardListingWrapper = styled.div`
  padding: 80px;
  @media (max-width: ${screenSizes.L}px) {
    padding: 1rem;
    margin-top: 1rem;
  }
  input {
    background-color: #091c000d;
  }
`;

const ParentCardsBox = styled.div<{ showFilter: boolean }>`
  margin-top: 20px;
  display: grid;
  grid-template-columns: ${({ showFilter }) =>
    showFilter ? "repeat(4, 1fr)" : "repeat(5, 1fr)"};
  gap: 24px;
  @media (max-width: ${screenSizes.L}px) {
    grid-template-columns: auto;
  }
`;

const Card = styled.div`
  cursor: pointer;
  padding: 12px;
  background-color: #fff;
  border: 1px solid rgba(9, 28, 0, 0.1);
  @media (max-width: ${screenSizes.L}px) {
    display: flex;
  }
  img {
    height: 224px;
    width: 100%;
    margin-bottom: 10px;
    background-size: cover;
    background-position: center;
    @media (max-width: ${screenSizes.L}px) {
      width: 100px;
      height: 107px;
      min-width: 100px;
      margin-bottom: 0px;
    }
  }
  .icon {
    margin-bottom: 2px;
  }
`;

const ContentContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-around;
  @media (max-width: ${screenSizes.L}px) {
    height: 100%;
  }
`;

const ContentWrapper = styled.div`
  display: block;
  @media (max-width: ${screenSizes.L}px) {
    padding-inline: 0.5rem;
  }
  h5 {
    margin: 0;
    font-family: "Manrope", sans-serif;
    font-size: 16px;
    font-weight: 700;
    color: rgba(9, 28, 0, 1);
    margin-bottom: 5px;
    display: -webkit-box; //  box layout for clamping
    -webkit-line-clamp: 1; // Limit to 2 lines
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
    word-wrap: break-word;
    @media (max-width: ${screenSizes.L}px) {
      font-size: 15px;
    }
  }
  h6 {
    margin: 0;
    font-family: "Manrope", sans-serif;
    font-size: 16px;
    font-weight: 400;
    color: rgba(9, 28, 0, 1);
    @media (max-width: ${screenSizes.L}px) {
      font-size: 12px;
    }
  }
`;
const LoaderWrapper = styled.div`
  margin-top: 40px;
`;

const NoResults = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 15px;
`;

const ButtonWrapper = styled.div`
  text-align: center;
  margin: 40px 0px;
`;

const PriceAndScore = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  @media (max-width: ${screenSizes.L}px) {
    padding-inline: 0.5rem;
    padding-top: 5px;
  }
`;

const Rating = styled.div<{ value: number }>`
  display: flex;
  align-items: center;
  background-color: ${({ value }) =>
    value <= 3 ? "#C7FFC5" : value >= 6 ? "#FFC5C5" : "#feffc5"};
  border: 1px solid rgba(9, 28, 0, 0.1);
  padding: 0 8px;
  border-radius: 40px;
  min-width: 70px;
  justify-content: center;
  span {
    font-size: 14px;
    font-weight: 400;
    color: #091c00;
  }
`;

interface Props {
  aiRankingNFTs: AIRankingNFTs[] | [];
  isLoading: boolean;
  currentPage: number;
  totalPages: number;
  getMoreAIRankingNFTs: (
    name: string,
    rank_by: string,
    limit?: number,
    pageNo?: number,
    isLoadingMore?: boolean
  ) => void;
  isLoadingMore: boolean;
  search?: string;
  handleSearch?: (
    query: string,
    limit?: number,
    pageNo?: number,
    isLoadingMore?: boolean
  ) => void;
}

const CollectionNFTsSection: React.FC<Props> = ({
  aiRankingNFTs,
  isLoading,
  currentPage,
  totalPages,
  getMoreAIRankingNFTs,
  isLoadingMore,
  search,
  handleSearch,
}) => {
  const navigate = useNavigate();
  const { name, impactScore } = useSelector(
    (state: RootState) => state.CollectionNFTFilter
  );
  const [imageLoaded, setImageLoaded] = useState(false);
  const [showFilter, setShowFilter] = useState(false);

  const handleImageLoad = () => {
    setImageLoaded(true);
  };

  const handleLoadMore = () => {
    if (currentPage < totalPages) {
      if (search && handleSearch) {
        handleSearch(search, 10, currentPage + 1, true);
      } else {
        getMoreAIRankingNFTs(name, impactScore, 10, currentPage + 1, true);
      }
    }
  };

  const handlePrice = (networkId: string|number, price: string|number) => {
    let calculatedPrice = (networkId === 1)
    ? Number(price).toFixed(6)
    : (networkId === 295)
    ? Number(price)
    : Number(price).toFixed(6);
    return calculatedPrice;
  }

  return (
    <CardListingWrapper>
      <HeaderWithFilter
        heading={`All Digital Asset’s in this Collection`}
        subHeading="Explore our various Digital Asset listings"
        getFilteredNFTs={getMoreAIRankingNFTs}
      />
      <SearchBar
        handleSearch={handleSearch}
        placeholder="Search for Digital Asset’s or users"
        isHeaderSearch={false}
      />
      <FilterWrapperCard showFilter={showFilter}>
        {isLoading ? (
          <LoaderWrapper>
            <Loader />
          </LoaderWrapper>
        ) : aiRankingNFTs.length > 0 ? (
          <ParentCardsBox showFilter={showFilter}>
            {aiRankingNFTs.map((item: any) => (
              <Card onClick={() => navigate(`/nft-details/${item?.id}`)}>
                {!imageLoaded && (
                  <Skeleton
                    variant="rectangular"
                    width="100%"
                    height={200}
                    animation="wave"
                    sx={{
                      marginBottom: 2,
                    }}
                  />
                )}
                <img
                  src={getValidImageUrl(item?.nftImage, BlankImage)}
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.src = BlankImage;
                  }}
                  alt="nft_image"
                  style={{ display: imageLoaded ? "block" : "none" }}
                  onLoad={handleImageLoad}
                />
                <ContentContainer>
                <ContentWrapper>
                  <FireIcon className="icon" />
                  <h5 title={item?.nftName}>{item?.nftName}</h5>
                  {/* <h6>
                    {typeof item?.ecoScore === "number"
                      ? item.ecoScore.toFixed(2)
                      : "0.00"}{" "}
                    ETH
                  </h6> */}
                </ContentWrapper>
                <PriceAndScore>
                  <h6
                    style={{
                      margin: 0,
                      fontSize: "16px",
                      fontWeight: 400,
                      color: "#091C00",
                    }}
                  >
                    {handlePrice(item.networkId, item.price)}{" "}
                    {item?.networkId === 1
                      ? "ETH"
                      : item?.networkId === 137
                      ? "POL"
                      : "HBAR"}
                  </h6>
                  <Rating value={item?.ecoScore}>
                    <LeafIcon />
                    <span>
                      {typeof item?.ecoScore === "number"
                        ? item.ecoScore.toFixed(1)
                        : "0"}
                    </span>
                  </Rating>
                </PriceAndScore>
                </ContentContainer>
              </Card>
            ))}
          </ParentCardsBox>
        ) : (
          <NoResults>No Results Found!</NoResults>
        )}
        {showFilter && <FilterForm showFilter={showFilter} />}
      </FilterWrapperCard>
      {isLoadingMore && (
        <LoaderWrapper>
          <Loader />
        </LoaderWrapper>
      )}
      {aiRankingNFTs.length > 0 &&
        !isLoadingMore &&
        currentPage < totalPages && (
          <ButtonWrapper>
            <IconButtonSmall
              icon={<ArrowForwardOrange />}
              onClick={handleLoadMore}
            >
              View More
            </IconButtonSmall>
          </ButtonWrapper>
        )}
    </CardListingWrapper>
  );
};

export default CollectionNFTsSection;
